import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAgentAuth } from "../context/AuthContext";
import { PaymentIcon } from "../../components/profile/PaymentIcons";
import { WalletBrandLogo } from "../../components/profile/WalletBrandLogos";
import {
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  RefreshCw,
  ArrowLeft,
  Wallet,
} from "lucide-react";

const fmtBdt = (n) =>
  `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso || ""; }
};

const STATUS = {
  completed: { color: "#10b981", label: "Payment Confirmed", Icon: CheckCircle2, sub: "Your BDT balance has been credited." },
  pending:   { color: "#f59e0b", label: "Awaiting Confirmation", Icon: Clock, sub: "We're waiting for the gateway to confirm your payment. This usually takes a few seconds." },
  failed:    { color: "#ef4444", label: "Payment Failed", Icon: XCircle, sub: "The gateway reported a failure. No balance was credited." },
  rejected:  { color: "#ef4444", label: "Payment Rejected", Icon: XCircle, sub: "The gateway rejected this transaction. No balance was credited." },
};

export default function BdtDepositReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAgentAuth();
  const ref = params.get("ref") || "";

  const [deposit, setDeposit] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  const load = async (showSpinner = false) => {
    if (!ref) return;
    if (showSpinner) setPolling(true);
    try {
      const { data } = await api.get(`/agent/bdt-deposit/status/${ref}`);
      setDeposit(data?.deposit || null);
      if (typeof data?.balance === "number") setBalance(data.balance);
    } catch (err) {
      // 404 → unknown deposit
      setDeposit(null);
    } finally {
      setLoading(false);
      if (showSpinner) setPolling(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  // Auto-poll while pending (every 4s, up to 90s)
  useEffect(() => {
    if (!deposit || deposit.status !== "pending") {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    const start = Date.now();
    pollRef.current = setInterval(() => {
      if (Date.now() - start > 90_000) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        return;
      }
      load();
    }, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit?.status]);

  // Refresh global balance pill when status becomes completed
  useEffect(() => {
    if (deposit?.status === "completed") {
      refresh?.();
    }
  }, [deposit?.status, refresh]);

  const meta = useMemo(() => {
    const st = (deposit?.status || "pending").toLowerCase();
    return STATUS[st] || STATUS.pending;
  }, [deposit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="agent-bdt-return-loading">
        <Loader2 className="animate-spin text-emerald-400" size={28} />
      </div>
    );
  }

  if (!deposit) {
    return (
      <div className="max-w-xl mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/10 p-6 text-amber-200" data-testid="agent-bdt-return-notfound">
        <div className="font-bold mb-1">Deposit not found</div>
        <div className="text-sm text-amber-100/85 mb-4">
          We couldn&apos;t find a deposit with reference <code className="font-mono">{ref}</code>.
        </div>
        <Link
          to="/agent/bdt-deposit"
          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-white/10 bg-white/[0.04] text-[12px] text-white/80 hover:text-white hover:bg-white/[0.08] transition"
        >
          <ArrowLeft size={14} /> Back to BDT Deposit
        </Link>
      </div>
    );
  }

  const { Icon } = meta;

  return (
    <div className="max-w-2xl mx-auto" data-testid="agent-bdt-return-page">
      <div
        className="rounded-2xl border p-7 text-center"
        style={{
          borderColor: `${meta.color}33`,
          background: `linear-gradient(180deg, ${meta.color}10 0%, rgba(13,22,38,0.6) 60%)`,
        }}
      >
        {/* Brand tile — hand-coded brand-faithful logo */}
        {(deposit.gateway_method || deposit.channel) && (
          <div className="flex justify-center mb-4" data-testid="agent-bdt-return-brand-tile">
            <WalletBrandLogo id={deposit.gateway_method || deposit.channel} size={64} />
          </div>
        )}

        <div
          className="mx-auto h-16 w-16 rounded-2xl grid place-items-center mb-4"
          style={{ background: `${meta.color}22`, color: meta.color }}
        >
          <Icon size={36} strokeWidth={2.4} />
        </div>
        <div className="text-[10px] uppercase tracking-[2px] font-bold mb-1" style={{ color: meta.color }}>
          BDT Deposit
        </div>
        <h1 className="text-[22px] sm:text-[26px] font-extrabold tracking-tight" data-testid="agent-bdt-return-status">
          {meta.label}
        </h1>
        <p className="text-[13px] text-white/65 mt-2 max-w-md mx-auto">{meta.sub}</p>

        {/* Amount */}
        <div className="mt-5 inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-white/[0.04] border border-white/10">
          <span className="text-[10px] uppercase tracking-[1.5px] text-white/45 font-bold">Amount</span>
          <span className="font-mono font-extrabold text-white tabular-nums">
            {fmtBdt(deposit.credited_amount || deposit.amount)}
          </span>
        </div>

        {/* Updated balance */}
        {deposit.status === "completed" && typeof balance === "number" && (
          <div
            className="mt-3 inline-flex items-center gap-2 px-4 h-10 rounded-xl border"
            style={{ borderColor: "#10b98144", background: "#10b9811a" }}
            data-testid="agent-bdt-return-balance"
          >
            <Wallet size={14} className="text-emerald-300" />
            <span className="text-[11px] uppercase tracking-[1.5px] font-bold text-emerald-300">
              New balance
            </span>
            <span className="font-mono font-extrabold tabular-nums text-emerald-200">{fmtBdt(balance)}</span>
          </div>
        )}

        {/* Meta grid */}
        <div className="mt-6 grid grid-cols-2 gap-3 text-left">
          <Meta label="Gateway"   value={deposit.gateway_label || deposit.provider} />
          <Meta label="Method"    value={deposit.channel_label || deposit.method} />
          <Meta label="Reference" value={deposit.reference || deposit.id?.slice(0, 12)} mono />
          <Meta label="Created"   value={fmtDate(deposit.created_at)} />
        </div>

        {/* Actions */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {deposit.status === "pending" && (
            <button
              type="button"
              onClick={() => load(true)}
              data-testid="agent-bdt-return-refresh"
              className="h-10 px-4 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] text-[12px] text-white/80 hover:text-white hover:bg-white/[0.08] transition"
            >
              {polling ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Check again
            </button>
          )}
          {deposit.status === "pending" && deposit.redirect_url && (
            <a
              href={deposit.redirect_url}
              className="h-10 px-4 inline-flex items-center gap-2 rounded-lg bg-amber-500/15 border border-amber-400/40 text-amber-200 text-[12px] font-bold hover:bg-amber-500/25 transition"
              data-testid="agent-bdt-return-resume"
            >
              Resume payment
            </a>
          )}
          <Link
            to="/agent/bdt-deposit"
            data-testid="agent-bdt-return-back"
            className="h-10 px-4 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] text-[12px] text-white/80 hover:text-white hover:bg-white/[0.08] transition"
          >
            <ArrowLeft size={14} /> Back to BDT Deposit
          </Link>
          {deposit.status === "completed" && (
            <button
              type="button"
              onClick={() => navigate("/agent/dashboard")}
              data-testid="agent-bdt-return-dashboard"
              className="h-10 px-4 inline-flex items-center gap-2 rounded-lg text-[12px] font-extrabold transition"
              style={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "#04130a",
                boxShadow: "0 12px 28px -10px rgba(16,185,129,0.55)",
              }}
            >
              Go to dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-2">
      <div className="text-[9.5px] uppercase tracking-[1.5px] font-bold text-white/45 mb-0.5">{label}</div>
      <div className={`text-[12px] text-white/85 ${mono ? "font-mono break-all" : ""}`}>{value || "—"}</div>
    </div>
  );
}
